import { Check, ChevronDown, ChevronUp } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { formatUzPhone, phoneSearchQuery } from "../../../phone";
import { t as translate } from "../preferences";

export type CustomerMode = "anonymous" | "existing" | "new";
type Customer = { id: number; full_name: string; phone: string };

export function CustomerCombobox({ clients, mode, clientId, onSelect, allowAnonymous = true, label = translate("Xaridor") }: {
  clients: Customer[]; mode: CustomerMode; clientId: string; allowAnonymous?: boolean; label?: string;
  onSelect: (mode: CustomerMode, clientId: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const listId = useId();
  const visible = useMemo(() => {
    const name = query.trim().toLocaleLowerCase("uz"), phone = phoneSearchQuery(query);
    return !name ? clients : clients.filter(client => client.full_name.toLocaleLowerCase("uz").includes(name)
      || phoneSearchQuery(client.phone).includes(phone));
  }, [clients, query]);
  const selected = mode === "anonymous" ? translate(allowAnonymous ? "Ko‘rsatilmagan" : "Tanlang") : mode === "new" ? translate("Yangi")
    : clients.find(client => String(client.id) === clientId)?.full_name ?? translate("Tanlang");
  function choose(next: CustomerMode, id = "") {
    onSelect(next, id); setOpen(false); setQuery("");
  }
  return <div onBlurCapture={event => {
    const next = event.relatedTarget as Node | null;
    if (!next || !event.currentTarget.contains(next)) setOpen(false);
  }} onKeyDown={event => { if (open && event.key === "Escape") { event.preventDefault(); event.stopPropagation(); setOpen(false); } }}>
    <span className="m-field-label">{label}</span>
    <button type="button" role="combobox" aria-label={label} aria-expanded={open} aria-controls={open ? listId : undefined}
      className="m-text-input m-flex m-items-center m-justify-between m-gap-2 m-text-left" onClick={() => setOpen(value => !value)}>
      <span className="m-truncate">{selected}</span>{open ? <ChevronUp className="m-size-5 m-shrink-0" /> : <ChevronDown className="m-size-5 m-shrink-0" />}
    </button>
    {open ? <div className="m-mt-1 m-rounded-xl m-border m-border-border m-bg-surface m-p-2">
      <input type="search" className="m-text-input m-mb-2" aria-label={translate("Mijoz qidirish")} placeholder={translate("Nomi yoki telefon raqami")}
        autoFocus value={query} onChange={event => setQuery(event.target.value)} />
      <div id={listId} role="listbox" aria-label={label} className="m-max-h-60 m-overflow-y-auto m-rounded-xl m-border m-border-border">
        {allowAnonymous ? <CustomerOption selected={mode === "anonymous"} label={translate("Ko‘rsatilmagan")} onClick={() => choose("anonymous")} /> : null}
        <CustomerOption selected={mode === "new"} label={translate("Yangi")} onClick={() => choose("new")} />
        {visible.map(client => <CustomerOption key={client.id} selected={mode === "existing" && clientId === String(client.id)}
          label={client.full_name} detail={formatUzPhone(client.phone)} onClick={() => choose("existing", String(client.id))} />)}
      </div>
      {query.trim() && !visible.length ? <p className="m-p-3 m-text-center m-text-sm m-text-muted">{translate("Mos mijoz topilmadi.")}</p> : null}
    </div> : null}
  </div>;
}

function CustomerOption({ selected, label, detail, onClick }: { selected: boolean; label: string; detail?: string; onClick: () => void }) {
  return <button type="button" role="option" aria-selected={selected} className="m-flex m-min-h-12 m-w-full m-items-center m-gap-2 m-border-b m-border-border m-px-3 m-py-2 m-text-left last:m-border-b-0" onClick={onClick}>
    <Check className={`m-size-5 m-shrink-0 m-text-primary ${selected ? "m-opacity-100" : "m-opacity-0"}`} />
    <span className="m-min-w-0"><span className="m-block m-truncate">{label}</span>{detail ? <span className="m-block m-text-sm m-text-muted">{detail}</span> : null}</span>
  </button>;
}
